/**
 * Follow FHIR Bundle pagination links until all entries are collected.
 * FHIR servers default to ~20 results per page; callers must paginate to avoid truncated lists.
 */
export async function fetchAllFhirBundleEntries(initialUrl, headers, options = {}) {
  const { maxPages = 25, httpClient = fetch } = options;
  const visited = new Set();
  let nextUrl = initialUrl;
  let pageCount = 0;
  const entries = [];

  while (nextUrl) {
    if (visited.has(nextUrl) || pageCount >= maxPages) {
      break;
    }
    visited.add(nextUrl);
    pageCount += 1;

    const response = await httpClient(nextUrl, { headers });
    if (!response.ok) {
      throw new Error(`FHIR request failed (${response.status})`);
    }

    const bundle = await response.json();
    if (Array.isArray(bundle.entry)) {
      entries.push(...bundle.entry);
    }

    nextUrl = null;
    const nextLink = bundle.link?.find((link) => link.relation === 'next');
    if (nextLink?.url) {
      nextUrl = nextLink.url.replace(/^http:\/\//i, 'https://');
    }
  }

  return entries;
}

/**
 * Count FHIR resources using _summary=count when supported, with paginated fallback.
 */
export async function fetchFhirResourceCount(baseUrl, params, headers, options = {}) {
  const { httpClient = fetch } = options;
  const countUrl = `${baseUrl}?${params}&_summary=count`;

  const countResponse = await httpClient(countUrl, { headers });
  if (countResponse.ok) {
    const bundle = await countResponse.json();
    if (Number.isFinite(bundle.total)) {
      return bundle.total;
    }
  }

  const entries = await fetchAllFhirBundleEntries(`${baseUrl}?${params}&_count=100`, headers, { httpClient });
  return entries.length;
}
